import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';

// Demo UI (account list, seed button, prefilled shared password) is gated behind
// an explicit build-time flag. When REACT_APP_SHOW_DEMO_ACCOUNTS !== 'true' (the
// production default because the env var is absent), the login page ships without
// any demo shortcut, without the shared password prefill, and without the "Seed
// demo accounts" link. Additionally the backend gates POST /api/bootstrap behind
// ALLOW_BOOTSTRAP=true, so even if the button existed it would return 403.
const SHOW_DEMO = process.env.REACT_APP_SHOW_DEMO_ACCOUNTS === 'true';

const DEMO_ACCOUNTS = [
  ['Owner / Admin', 'owner@velvetbloom.test'],
  ['Manager / Dispatcher', 'manager@velvetbloom.test'],
  ['Sales / POS', 'sales@velvetbloom.test'],
  ['Designer', 'designer@velvetbloom.test'],
  ['Driver', 'driver@velvetbloom.test'],
  ['Purchasing / Nexa', 'purchasing@velvetbloom.test'],
];

export default function Login() {
  const { session, signIn, loading, authError, profile, recoveryPending } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(SHOW_DEMO ? 'VelvetOS2026!' : '');
  const [busy, setBusy] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);

  // If this session was born from a recovery link, never treat it as a real login.
  if (recoveryPending) return <Navigate to="/auth/callback" replace />;
  // Never render blank: while auth boots, show a tiny inline hint instead of null.
  // Only redirect away from /login when we have a fully-hydrated profile AND no error.
  if (session && profile && !authError) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      toast.success('Signed in');
    } catch (err) {
      toast.error(err.message || 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const seed = async () => {
    setSeedBusy(true);
    try {
      const res = await fetch(`${API}/bootstrap`, { method: 'POST' });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.detail || 'Seed failed');
      toast.success(`Seed complete — ${j.created_users?.length || 0} accounts ready`);
    } catch (e) {
      toast.error(e.message || 'Seed failed');
    } finally {
      setSeedBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[hsl(var(--soft-blush))]/60 border-r border-border">
        <div>
          <div className="velvet-eyebrow text-foreground/60">Velvet Bloom Flowers</div>
          <div className="font-serif text-6xl leading-none tracking-tight text-foreground mt-2">
            Velvet <span className="italic text-[hsl(var(--muted-green-dark))]">OS</span>
          </div>
          <p className="mt-8 max-w-md text-sm text-muted-foreground leading-relaxed">
            The atelier's private operating system — customers, quotations, recipes, production,
            and delivery, kept quiet and precise.
          </p>
        </div>
        {SHOW_DEMO && (
          <div className="border border-border bg-card p-6" data-testid="demo-accounts-panel">
            <div className="velvet-eyebrow mb-3">Phase 1 demo accounts</div>
            <ul className="text-sm space-y-1.5">
              {DEMO_ACCOUNTS.map(([r, e]) => (
                <li key={e} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{r}</span>
                  <button className="font-mono text-xs hover:underline" onClick={() => setEmail(e)}>{e}</button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground mt-4">
              Shared demo password: <span className="font-mono">VelvetOS2026!</span>
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <div className="velvet-eyebrow mb-2">Sign in</div>
            <h1 className="font-serif text-4xl tracking-tight">Welcome back.</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {SHOW_DEMO ? 'Use a role account to explore Phase 1.' : 'Enter your Velvet OS credentials.'}
            </p>
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   required autoFocus className="rounded-sm" data-testid="login-email" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   required className="rounded-sm" data-testid="login-password" />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="login-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
          </Button>
          {SHOW_DEMO && (
            <div className="text-xs text-muted-foreground text-center">
              First run? <button type="button" onClick={seed} disabled={seedBusy} className="underline hover:text-foreground" data-testid="seed-demo-btn">
                {seedBusy ? 'Seeding…' : 'Seed demo accounts'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
